/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { Box, Button, Select } from "@chakra-ui/react";
import { Tooltip } from "@mui/material";
import InputField from "components/fields/InputField";
import MerchantListModal from "../../shared/MerchantListModal";
import SubaccountListModal from "../../shared/SubaccountListModal";
import AgentListModal from "../../shared/AgentListModal";

interface UserTableSearchProps {
  /** Make optional so parent doesn't have to pass it */
  showFilters?: boolean;
  initialFilters: any;
  filters: any;
  setFilters: any;
  onSearch: () => void;
  onClear: () => void;
  isLoading: boolean;
  isConfirming: boolean;
}

const UserTableSearch: React.FC<UserTableSearchProps> = ({
  showFilters = true, // default so TS2741 goes away and UI still renders
  initialFilters,
  filters,
  setFilters,
  onSearch,
  onClear,
  isLoading,
  isConfirming,
}) => {
  // Modal states for merchant, subaccount, and agent selection
  const [isMerchantModalOpen, setIsMerchantModalOpen] = useState<boolean>(false);
  const [isSubaccountModalOpen, setIsSubaccountModalOpen] = useState<boolean>(false);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState<boolean>(false);
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);
  const [selectedSubaccount, setSelectedSubaccount] = useState<any>(null);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  // Handler for merchant selection from modal
  const handleMerchantSelect = (merchantData: any) => {
    setSelectedMerchant(merchantData);
    // Clear subaccount (dependent on merchant)
    setSelectedSubaccount(null);
    // Update filters with merchant_id, clear subaccount_id
    setFilters({ ...filters, merchant_id: merchantData.id, subaccount_id: "" });
  };

  // Clear merchant selection
  const handleClearMerchant = () => {
    setSelectedMerchant(null);
    setSelectedSubaccount(null);
    setFilters({ ...filters, merchant_id: "", subaccount_id: "" });
  };

  // Handler for subaccount selection from modal
  const handleSubaccountSelect = (subaccountData: any) => {
    setSelectedSubaccount(subaccountData);
    setFilters({ ...filters, subaccount_id: subaccountData.id });
  };

  // Clear subaccount selection
  const handleClearSubaccount = () => {
    setSelectedSubaccount(null);
    setFilters({ ...filters, subaccount_id: "" });
  };

  // Handler for agent selection from modal
  const handleAgentSelect = (agentData: any) => {
    setSelectedAgent(agentData);
    setFilters({ ...filters, agent_id: agentData.id });
  };

  // Clear agent selection
  const handleClearAgent = () => {
    setSelectedAgent(null);
    setFilters({ ...filters, agent_id: "" });
  };

  const handleSearch = () => onSearch();
  const handleClear = () => {
    // Clear merchant, subaccount, and agent selections
    setSelectedMerchant(null);
    setSelectedSubaccount(null);
    setSelectedAgent(null);
    onClear();
  };

  if (!showFilters) return null;

  return (
    <Box className="mb-4 mt-2 rounded-lg bg-white p-6 shadow-lg ring-1 ring-gray-100">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
        {/* Email Filter */}
        <div className="tracking-tight text-gray-900">
          <InputField
            disabled={isLoading || isConfirming}
            variant="auth"
            extra="shadow-sm"
            label="Email"
            placeholder=""
            id="email"
            name="email"
            type="text"
            autoComplete="off"
            value={filters.email}
            onChange={handleChange}
          />
        </div>

        {/* Username Filter */}
        <div className="tracking-tight text-gray-900">
          <InputField
            disabled={isLoading || isConfirming}
            variant="auth"
            extra="shadow-sm"
            label="Username"
            placeholder=""
            id="username"
            name="username"
            type="text"
            autoComplete="off"
            value={filters.username}
            onChange={handleChange}
          />
        </div>

        {/* Merchant Filter */}
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Merchant
          </label>
          <Tooltip
            title={selectedMerchant ? selectedMerchant.name : "All"}
            arrow
            placement="top"
          >
            <div
              onClick={() => !(isLoading || isConfirming) && setIsMerchantModalOpen(true)}
              className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                isLoading || isConfirming
                  ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                  : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
              }`}
            >
              <span className="truncate">
                {selectedMerchant ? selectedMerchant.name : "All"}
              </span>
              {selectedMerchant && !(isLoading || isConfirming) && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleClearMerchant(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                >
                  &times;
                </button>
              )}
            </div>
          </Tooltip>
        </div>

        {/* Subaccount Filter */}
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Subaccount
          </label>
          <Tooltip
            title={selectedSubaccount ? selectedSubaccount.suba_name : "All"}
            arrow
            placement="top"
          >
            <div
              onClick={() => !(isLoading || isConfirming) && selectedMerchant && setIsSubaccountModalOpen(true)}
              className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                isLoading || isConfirming || !selectedMerchant
                  ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                  : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
              }`}
            >
              <span className="truncate">
                {selectedSubaccount ? selectedSubaccount.suba_name : "All"}
              </span>
              {selectedSubaccount && !(isLoading || isConfirming) && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleClearSubaccount(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                >
                  &times;
                </button>
              )}
            </div>
          </Tooltip>
        </div>

        {/* Agent Filter */}
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Agent
          </label>
          <Tooltip
            title={selectedAgent ? selectedAgent.suba_name : "All"}
            arrow
            placement="top"
          >
            <div
              onClick={() => !(isLoading || isConfirming) && setIsAgentModalOpen(true)}
              className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                isLoading || isConfirming
                  ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                  : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
              }`}
            >
              <span className="truncate">
                {selectedAgent ? selectedAgent.suba_name : "All"}
              </span>
              {selectedAgent && !(isLoading || isConfirming) && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleClearAgent(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                >
                  &times;
                </button>
              )}
            </div>
          </Tooltip>
        </div>

        {/* Status Filter */}
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Status
          </label>
          <Select
            isDisabled={isLoading || isConfirming}
            id="status"
            name="status"
            value={filters.status}
            onChange={handleSelectChange}
            className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
          >
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </Select>
        </div>
      </div>

      <div className="mt-4 flex flex-1 items-end justify-end gap-2">
        <Button
          isDisabled={isLoading || isConfirming}
          onClick={handleClear}
          variant="solid"
          size="sm"
          className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
        >
          Clear
        </Button>

        <Button
          isDisabled={isLoading || isConfirming}
          onClick={handleSearch}
          variant="solid"
          size="sm"
          className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="relative z-10 tracking-tight">Search Users</span>
          <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
        </Button>
      </div>

      {/* Merchant Selection Modal */}
      {isMerchantModalOpen && (
        <MerchantListModal
          isOpen={isMerchantModalOpen}
          onClose={() => setIsMerchantModalOpen(false)}
          onSelect={handleMerchantSelect}
        />
      )}

      {/* Subaccount Selection Modal */}
      {isSubaccountModalOpen && selectedMerchant && (
        <SubaccountListModal
          isOpen={isSubaccountModalOpen}
          onClose={() => setIsSubaccountModalOpen(false)}
          onSelect={handleSubaccountSelect}
          merchantId={selectedMerchant.id}
        />
      )}

      {/* Agent Selection Modal */}
      {isAgentModalOpen && (
        <AgentListModal
          isOpen={isAgentModalOpen}
          onClose={() => setIsAgentModalOpen(false)}
          onSelect={handleAgentSelect}
        />
      )}
    </Box>
  );
};

export default UserTableSearch;
